import React from 'react';

import {IdcardOutlined, LockOutlined} from '@ant-design/icons/lib';
import {Button, Card} from 'antd';
import {fetchAuthSession, signIn, signOut} from 'aws-amplify/auth';
import type {FormikProps} from 'formik';
import {withFormik} from 'formik';
import {FormItem, Input} from 'formik-antd';
import * as yup from 'yup';

import {AnchorLink} from '../AnchorLink';

import {Extra} from './Shared';
import {fetchCurrentUser, isPasswordExpiring} from './utils';

import {useRecaptcha} from 'hooks/useRecaptcha';
import {getErrorInfo} from 'lib/errorUtils';
import type {IAuthProps} from 'types';
import {AuthState, MfaType} from 'types';

interface IFormValues {
  email: string;
  password: string;
}

interface SignInFormProps extends IAuthProps {
  executeRecaptcha: () => Promise<string | null>;
}

const SignInFormComponent: React.FC<FormikProps<IFormValues> & SignInFormProps> = (props) => {
  const {isSubmitting, values, handleSubmit, setState} = props;

  const resetPasswordLink = (e: React.SyntheticEvent) => {
    e.preventDefault();
    setState({authState: AuthState.forgotPassword, email: values.email});
  };

  return (
    <Card>
      <form
        className="ant-form ant-form-vertical"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}>
        <FormItem name="email" label="Email">
          <Input
            autoFocus
            name="email"
            type="email"
            autoComplete="email"
            size="large"
            prefix={<IdcardOutlined />}
          />
        </FormItem>
        <FormItem name="password" label="Password">
          <Input.Password
            name="password"
            type="password"
            autoComplete="current-password"
            size="large"
            prefix={<LockOutlined />}
          />
        </FormItem>
        <Button block size="large" type="primary" loading={isSubmitting} htmlType="submit">
          Sign In
        </Button>
        <Extra>
          Forgot your password? <AnchorLink onClick={resetPasswordLink}>Reset password</AnchorLink>
        </Extra>
      </form>
    </Card>
  );
};

SignInFormComponent.displayName = 'SignInFormComponent';

const schema = yup.object().shape({
  email: yup.string().email().required(),
  password: yup.string().min(6).required()
});

const SignInFormWithFormik = withFormik<SignInFormProps, IFormValues>({
  validationSchema: schema,
  mapPropsToValues: ({email = ''}) => ({email, password: ''}),
  handleSubmit: async (values, helpers) => {
    const {email, password} = values;
    const {
      props: {setState, executeRecaptcha},
      setErrors,
      setSubmitting
    } = helpers;

    const captcha = await executeRecaptcha();
    if (!captcha) {
      setErrors({email: 'reCAPTCHA validation failed. Please refresh and try again.'});
      setSubmitting(false);
      return;
    }

    try {
      // Sign out any existing user first
      try {
        await signOut();
      } catch {
        // Ignore errors if no user is signed in
      }

      const clientMetadata: Record<string, string> = {
        recaptchaToken: captcha,
        userAgent: navigator.userAgent
      };

      const {isSignedIn, nextStep} = await signIn({
        username: email,
        password,
        options: {
          clientMetadata
        }
      });

      if (isSignedIn) {
        // User is signed in, fetch attributes
        const currentUser = await fetchCurrentUser();
        if (currentUser) {
          // Check if password is expiring within 30 days
          const session = await fetchAuthSession();
          const passwordExpiresAt = session.tokens?.idToken?.payload['custom:pwd_expires_at'] as
            | string
            | undefined;

          if (passwordExpiresAt && isPasswordExpiring(passwordExpiresAt)) {
            setState({
              authState: AuthState.passwordExpiryWarning,
              currentUser,
              email,
              passwordExpiresAt
            });
            return;
          }

          setState({authState: AuthState.signedIn, currentUser, email});
        }
      } else {
        switch (nextStep.signInStep) {
          case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED': {
            setState({
              authState: AuthState.requireNewPassword,
              currentUser: {isSignedIn, nextStep},
              email
            });
            return;
          }
          case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
          case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE': {
            setState({
              email,
              password, // Pass password transiently for MFA resend capability
              authState: AuthState.confirmSignIn,
              currentUser: {isSignedIn, nextStep},
              mfaDestination:
                nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE'
                  ? (nextStep as {codeDeliveryDetails?: {destination?: string}}).codeDeliveryDetails
                      ?.destination
                  : undefined,
              mfaType:
                nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE'
                  ? MfaType.SMS_MFA
                  : MfaType.SOFTWARE_TOKEN_MFA
            });
            return;
          }
        }
      }
    } catch (error: unknown) {
      // Safely extract error information
      const {name: errorName, message: errorMessage} = getErrorInfo(error);

      // If user is not confirmed, show error message instead of signup flow
      if (errorName === 'UserNotConfirmedException') {
        setErrors({password: 'Account not confirmed. Please contact your administrator.'});
        return;
      }
      if (errorName === 'NotAuthorizedException') {
        setErrors({password: errorMessage});
        return;
      }
      setErrors({password: errorMessage});
    } finally {
      setSubmitting(false);
    }
  }
})(SignInFormComponent);

SignInFormWithFormik.displayName = 'SignInFormWithFormik';

export const SignInForm: React.FC<IAuthProps> = (props) => {
  const {executeRecaptcha} = useRecaptcha();
  return <SignInFormWithFormik {...props} executeRecaptcha={executeRecaptcha} />;
};

SignInForm.displayName = 'SignInForm';
